import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaCalendarAlt, FaBuilding, FaTag, FaCheckCircle } from 'react-icons/fa';

// Assuming seoMeta.ts is in shared/seo/
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotFound from '../../../shared/components/NotFound/NotFound';
import Seo from '../../../shared/seo/Seo';
import { buildBreadcrumbJsonLd } from '../../../shared/seo/jsonLd';
import { getCanonicalUrl } from '../../../shared/seo/seoMeta';
import { createProjectsData, Project } from '../data/projectsData';

// Styled Components
const ProjectDetailContainer = styled.div`

  max-width: var(--container-lg);
  margin: 0 auto;
  padding: var(--spacing-xxl) var(--spacing-lg);

  @media (max-width: 768px) {
    padding: var(--spacing-xl) var(--spacing-md);
  }
`;

const Breadcrumbs = styled.div`
  margin-bottom: var(--spacing-md);
  font-size: 0.95rem;
  a {
    color: var(--primary);
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  span {
    color: var(--gray);
  }
`;

const ProjectHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xxl);
`;

const ProjectTitle = styled.h1`
  font-size: 2.8rem;
  font-weight: 700;
  color: var(--primary);
  margin-top: 4rem;
  margin-bottom: var(--spacing-md);

  @media (max-width: 768px) {
    font-size: 2.2rem;
    margin-top: 6rem;
  }





`;

const ProjectSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--gray);
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ProjectImageGallery = styled.div`
  height: 400px;
  border-radius: var(--border-radius);
  overflow: hidden;
  margin-bottom: var(--spacing-xxl);
  box-shadow: var(--shadow-lg);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 250px;
    margin-bottom: var(--spacing-xl);
  }
`;

const ProjectContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--spacing-xxl);

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }
`;

const ProjectDescriptionSection = styled.div`
  h2 {
    font-size: 1.8rem;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: var(--spacing-lg);
  }

  p {
    font-size: 1rem;
    line-height: 1.7;
    color: var(--text-color);
    margin-bottom: var(--spacing-md);
  }

  ul {
    list-style: none;
    padding-left: 0;
    margin-bottom: var(--spacing-md);
  }

    li {
      font-size: 1rem;
      line-height: 1.6;
      color: var(--text-color);
      margin-bottom: var(--spacing-sm);
      position: relative;
      padding-inline-start: 1.5rem;
      padding-inline-end: 0;
      text-align: start;

      &[dir="rtl"] {
        padding-inline-start: 0;
        padding-inline-end: 1.5rem;
        text-align: end;
        
        &::before {
          inset-inline-start: auto;
          inset-inline-end: 0;
        }
      }

      &::before {
        content: '•';
        color: var(--secondary);
        position: absolute;
        inset-inline-start: 0;
        top: 0.2rem;
      }
    }


`;

const ProjectDetailsSidebar = styled.div`
  background: var(--card-bg);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  min-height: 500px;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: var(--spacing-lg);
  }

  @media (max-width: 992px) {
    margin-top: var(--spacing-lg);
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  font-size: 1rem;
  color: var(--text-color);

  svg {
    color: var(--primary);
    font-size: 1.2rem;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
`;

const DetailValue = styled.span`
  color: var(--gray);
`;

const ProjectFeatures = styled.div`
  margin-top: var(--spacing-xxl);
  h2 {
    font-size: 1.8rem;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: var(--spacing-lg);
  }
  ul {
    list-style: none;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: var(--spacing-md);
  }
  li {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    color: var(--text-color);
    font-size: 1rem;
    flex-direction: row;
    
    &[dir="rtl"] {
      flex-direction: row-reverse;
    }
    
    svg {
      color: var(--secondary);
      flex-shrink: 0;
    }
  }

`;

const CallToActionBox = styled.div`
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  padding: var(--spacing-md);
  border-radius: 20px;
  color: var(--white);
  text-align: center;
  margin-top: var(--spacing-lg);
  max-width: 350px;
  margin-left: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);

  h3 {
    color: var(--white);
    margin-bottom: var(--spacing-sm);
    font-size: 1.3rem;
  }


  p {
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: var(--spacing-md);
    font-size: 0.95rem;
  }

  a {
    background: var(--white);
    color: var(--primary);
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: var(--border-radius);
    text-decoration: none;
    font-weight: 600;
    transition: var(--transition);
    font-size: 0.95rem;
    display: inline-block;

    &:hover {
      background: var(--light);
      color: var(--primary-dark);
    }
  }

  @media (max-width: 768px) {
    max-width: 100%;
    margin-left: 0;
    margin-top: var(--spacing-lg);
    padding: var(--spacing-md);
  }
`;

const projects = createProjectsData();

const ProjectDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const lang = i18n.language as keyof Project['translations'];


  const project = projects.find(p => p.id === id);

  // An unknown :id used to render a bare <p> at HTTP 200 with no meta at all —
  // a textbook soft 404. NotFound carries the noindex.
  if (!project) {
    return <NotFound />;
  }

  const translation = project.translations[lang] || project.translations['fr'];

  const seo = {
    title: `${translation.title} - A2S SARL`,
    description: translation.description.substring(0, 160),
    canonicalPath: `/projects/${project.id}`,
  };

  // Use translation fields
  const title = translation.title;
  const client = translation.client;
  const location = translation.location;
  const shortDesc = translation.description.substring(0, 150) + '...';
  const fullDesc = translation.description.replace(/\n/g, '<br/>');
  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        canonical={getCanonicalUrl(seo.canonicalPath)}
        image={project.image}
        type="article"
        jsonLd={[
          buildBreadcrumbJsonLd([
            { name: 'Accueil', path: '/' },
            { name: 'Projets', path: '/projects' },
            { name: title, path: seo.canonicalPath },
          ]),
        ]}
      />

      <ProjectDetailContainer>
        <Breadcrumbs>
          <Link to="/projects">Projets</Link> <span>/</span> {title}
        </Breadcrumbs>

        <ProjectHeader>
          <ProjectTitle>{title}</ProjectTitle>
          <ProjectSubtitle>{shortDesc}</ProjectSubtitle>
        </ProjectHeader>

        <ProjectImageGallery>
          <img src={require(`../../../assets/images/projects/${project.image}`)} alt={title} />
        </ProjectImageGallery>

        <ProjectContentGrid>
          <ProjectDescriptionSection>
            <div dangerouslySetInnerHTML={{ __html: fullDesc }} />

            <ProjectFeatures>
              <h2>{t('projects.detail.features.title')}</h2>
              <ul>
                <li><FaCheckCircle /> {t('projects.detail.features.conception')}</li>
                <li><FaCheckCircle /> {t('projects.detail.features.installation')}</li>
                <li><FaCheckCircle /> {t('projects.detail.features.miseEnService')}</li>
                <li><FaCheckCircle /> {t('projects.detail.features.conformite')}</li>
                <li><FaCheckCircle /> {t('projects.detail.features.maintenance')}</li>
                <li><FaCheckCircle /> {t('projects.detail.features.garantie')}</li>
              </ul>
            </ProjectFeatures>

          </ProjectDescriptionSection>

          <ProjectDetailsSidebar>
            <h2>{t('projects.detail.sidebar.title')}</h2>
            <DetailItem>
              <FaBuilding /> <DetailLabel>{t('projects.detail.sidebar.client')}</DetailLabel> <DetailValue>{client}</DetailValue>
            </DetailItem>
            <DetailItem>
              <FaMapMarkerAlt /> <DetailLabel>{t('projects.detail.sidebar.location')}</DetailLabel> <DetailValue>{location}</DetailValue>
            </DetailItem>
            <DetailItem>
              <FaCalendarAlt /> <DetailLabel>{t('projects.detail.sidebar.year')}</DetailLabel> <DetailValue>{project.year}</DetailValue>
            </DetailItem>
            <DetailItem>
              <FaTag /> <DetailLabel>{t('projects.detail.sidebar.category')}</DetailLabel> <DetailValue>{t(`services.categories.${project.category}`)}</DetailValue>

            </DetailItem>
            <CallToActionBox>
              <h3>{t('projects.detail.cta.title')}</h3>
              <p>{t('projects.detail.cta.description')}</p>
              <Link to="/contact">{t('projects.detail.cta.button')}</Link>

            </CallToActionBox>
            
          </ProjectDetailsSidebar>
        </ProjectContentGrid>
        
      </ProjectDetailContainer>
      
    </>
  );
};


export default ProjectDetailsPage;
